<template>
  <div class="article-template__content-wrap">
    <div
      :data-ref="$options.consts.REFS.ARTICLE_PAGE_CONTENT"
      class="article-template__content-with-sidebar"
    >
      <article-left-content
        :article="article"
        :show-disclaimer="showDisclaimer"
        v-on="$listeners"
        @enlarge="openFeaturedImage"
      >
        <slot :on-set-popup-image="setPopupImage" />
      </article-left-content>
      <div class="article-template__sidebar-wrapper">
        <a-visibility show :from="$breakpoint.desktopLg">
          <article-sidebar
            :data-ref="$options.consts.REFS.ARTICLE_PAGE_SIDEBAR"
            :most-viewed-articles="relatedArticles.MostViewedArticles"
            :latest-news-articles="relatedArticles.LatestArticles"
            :education-center-articles="relatedArticles.EducationArticles"
            :keep-reading-articles="relatedArticles.KeepReadingArticles"
            :trending-articles="relatedArticles.TrendingArticles"
            class="article-template__sidebar"
          />
        </a-visibility>
      </div>
    </div>
    <more-from-the-author
      v-if="isMoreFromTheAuthorVisible"
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.MORE_FROM_THE_AUTHOR
        )
      "
      :data-ref="$options.consts.REFS.ARTICLE_PAGE_MORE_FROM_THE_AUTHOR"
      :articles="relatedArticles.AuthorArticles"
      :is-more-than-one-author="isMoreThanOneAuthor"
      class="article-template__more-from-the-author section-top-margin"
      @mounted="
        $_lazyHydratedComponentState_onHydratedComponentMounted(
          $options.consts.HYDRATED_SECTION_NAME.MORE_FROM_THE_AUTHOR
        )
      "
    />
    <category-news
      v-if="relatedArticles.CategoryArticles.length"
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.CATEGORY_NEWS
        )
      "
      :data-ref="$options.consts.REFS.ARTICLE_PAGE_CATEGORY_NEWS"
      :articles="relatedArticles.CategoryArticles"
      :category-name="article.categoryName"
      :category-slug="article.categorySlug"
      class="article-template__category-news section-top-margin"
      @mounted="
        $_lazyHydratedComponentState_onHydratedComponentMounted(
          $options.consts.HYDRATED_SECTION_NAME.CATEGORY_NEWS
        )
      "
    />
    <featured-videos
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.FEATURED_VIDEOS
        )
      "
      :data-ref="$options.consts.REFS.ARTICLE_PAGE_FEATURED_VIDEOS"
      :videos="relatedVideos"
      class="article-template__featured-videos section-top-margin"
      @mounted="
        $_lazyHydratedComponentState_onHydratedComponentMounted(
          $options.consts.HYDRATED_SECTION_NAME.FEATURED_VIDEOS
        )
      "
    />
    <client-only>
      <fade-transition :duration="150">
        <a-visibility
          v-if="isNewsletterWidgetVisible"
          show
          :on="[$breakpoint.mobile]"
        >
          <black-newsletter-form-widget
            ref="newsletterWidget"
            :was-expanded.sync="wasNewsletterWidgetExpanded"
            @open-sign-in-form="openSignInForm"
            @open-sign-up-form="openSignUpForm"
          />
        </a-visibility>
      </fade-transition>
    </client-only>
    <a-image-download-modal v-if="popupImage" :image="popupImage" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { PROP_TYPES, propValidator } from '@/utils/validators'
import mixins from '@/utils/mixins'
import { REFS } from 'enums/external-refs'
import ArticleLeftContent from './ArticleLeftContent'
import ArticleSidebar from './ArticleSidebar'
import AImageDownloadModal from 'shared/AImageDownloadModal'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import { AUTH_TYPE, COMPONENT_BY_AUTH_TYPE } from 'enums/auth'
import { PENDING_ACTION } from 'enums/pending-actions'
import { MODAL } from 'modals/AModalWrapper'
import { RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME } from 'enums/article'

const HYDRATED_SECTION_NAME = {
  MORE_FROM_THE_AUTHOR: 'MORE_FROM_THE_AUTHOR',
  CATEGORY_NEWS: 'CATEGORY_NEWS',
  FEATURED_VIDEOS: 'FEATURED_VIDEOS'
}

export default {
  name: 'AArticleTemplate',
  components: {
    ArticleLeftContent,
    ArticleSidebar,
    MoreFromTheAuthor: hydrateWhenVisible(() => import('./MoreFromTheAuthor'), {
      props: ['articles', 'is-more-than-one-author']
    }),
    CategoryNews: hydrateWhenVisible(() => import('./CategoryNews'), {
      props: ['articles', 'category-name', 'category-slug']
    }),
    FeaturedVideos: hydrateWhenVisible(() => import('./FeaturedVideos'), {
      props: ['videos']
    }),
    BlackNewsletterFormWidget: hydrateWhenVisible(
      () => import('./BlackNewsletterFormWidget'),
      { props: ['was-expanded'] }
    ),
    AImageDownloadModal
  },
  mixins: [mixins.pendingAuthActions, mixins.lazyHydratedComponentState],
  props: {
    article: propValidator([PROP_TYPES.OBJECT]),
    relatedArticles: propValidator([PROP_TYPES.OBJECT]),
    relatedVideos: propValidator([PROP_TYPES.ARRAY]),
    articleSlug: propValidator([PROP_TYPES.STRING]),
    categorySlug: propValidator([PROP_TYPES.STRING]),
    subcategorySlug: propValidator([PROP_TYPES.STRING], false),
    isEbookLoaded: propValidator([PROP_TYPES.BOOLEAN], false, false),
    isEbookShouldBeRendered: propValidator([PROP_TYPES.BOOLEAN], false, false),
    showDisclaimer: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  consts: { REFS, HYDRATED_SECTION_NAME },
  data() {
    return {
      wasNewsletterWidgetExpanded: false,
      isNewsletterWidgetVisiblePreviousState: false,
      popupImage: null
    }
  },
  computed: {
    ...mapGetters({
      isAuthStatusRequested: 'auth/isAuthStatusRequested',
      userDetails: 'auth/userDetails',
      isAuthStatusRequestInProgress: 'auth/isAuthStatusRequestInProgress',
      isLoggedIn: 'auth/isLoggedIn',
      isFooterInView: 'element-visibility/isFooterInView',
      rectDataByDataRef: 'dom/rectDataByDataRef'
    }),
    isNewsletterWidgetVisible() {
      if (this.isLoggedIn && !this.userDetails.Id) {
        return this.isNewsletterWidgetVisiblePreviousState
      }

      const isMobileHeaderMenuOpened = !!this.rectDataByDataRef[
        REFS.HEADER_MOBILE_EXPANDED_MENU
      ]?.height

      return (
        (this.isAuthStatusRequested || !this.isAuthStatusRequestInProgress) &&
        !this.isFooterInView &&
        (!this.isLoggedIn || !this.userDetails.HasFmNewsletterSubscriptions) &&
        !isMobileHeaderMenuOpened
      )
    },
    isCompany() {
      return !!this.article.sponsorshipCompany
    },
    isMoreFromTheAuthorVisible() {
      return !this.isCompany && !!this.relatedArticles.AuthorArticles.length
    },
    isMoreThanOneAuthor() {
      return this.article.publishers.length > 1
    }
  },
  watch: {
    isNewsletterWidgetVisible: {
      handler(newVal) {
        this.isNewsletterWidgetVisiblePreviousState = newVal
      }
    }
  },
  methods: {
    rerenderNonHydratedComponents() {
      this.$_lazyHydratedComponentState_rerenderNonHydratedComponents(
        Object.values(HYDRATED_SECTION_NAME)
      )
    },
    setPopupImage(image) {
      this.popupImage = image
    },
    openFeaturedImage({ src }) {
      if (!this.article.featuredImage && !src) return

      const imageToDownload = this.article.featuredImage || {
        MediaType: 'Image',
        URL: src
      }
      this.setPopupImage(imageToDownload)

      this.$nextTick(() => {
        this.$helper.openModal(MODAL.IMAGE_DOWNLOAD)
      })
    },
    openSignInForm() {
      this.collapseWidget()
      this.$_pendingAuthActions_handleAction({
        actionType: PENDING_ACTION.OPEN_ARTICLE_NEWSLETTER_WIDGET
      })
    },
    openSignUpForm() {
      this.collapseWidget()
      this.$_pendingAuthActions_handleAction({
        actionType: PENDING_ACTION.OPEN_ARTICLE_NEWSLETTER_WIDGET,
        activeComponent: COMPONENT_BY_AUTH_TYPE[AUTH_TYPE.SIGN_UP]
      })
    },
    collapseWidget() {
      const newsletterWidget = this.$helper.getComponentByRef(
        this.$refs.newsletterWidget
      )

      if (!newsletterWidget) return

      newsletterWidget.collapseWidget()
    }
  },
  beforeMount() {
    this.$bus.$on(
      RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME,
      this.rerenderNonHydratedComponents
    )
  },
  beforeDestroy() {
    this.$bus.$off(
      RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME,
      this.rerenderNonHydratedComponents
    )
  }
}
</script>

<style scoped lang="scss">
@import '~assets/scss/local/mixins/grid';
@import '~assets/scss/local/styles/article';
@import '~assets/scss/global/mixins/maps';

.article-template__content-wrap {
  .article-template__content-with-sidebar {
    $grid-setup: (
      $columnSetup: (
        $above-desktop-xl: minmax(0, 100%) $article-sidebar-width,
        $desktop-md: minmax(0, 100%)
      ),
      $columnGapSetup: (
        $above-desktop-xl: 50px
      )
    );

    @include gridBuilder($grid-setup);

    align-items: start;
  }

  /deep/ .article-template__featured-videos {
    $margin-bottom: negativeMapValues(map-get($content-padding, $bottom));

    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }
}
</style>
